import { transCountryText } from '@op/utils';
import type {
  IXHashtagListResponse,
  IXHashtagMatrixColumn,
  IXHashtagMatrixRow,
  IXHashtagMatrixView,
  IXHashtagHeaderGroup,
} from './types';

function isDateKey(key: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(key);
}

function formatOuterTitle(
  outerKey: string,
  groupBy: 'date' | 'country',
): string {
  if (groupBy === 'date' && isDateKey(outerKey)) {
    return outerKey;
  }
  if (groupBy === 'country') {
    return transCountryText(outerKey) || outerKey;
  }
  return outerKey;
}

function formatInnerTitle(
  innerKey: string,
  groupBy: 'date' | 'country',
): string {
  if (groupBy === 'country' && isDateKey(innerKey)) {
    return innerKey;
  }
  if (groupBy === 'date') {
    return transCountryText(innerKey) || innerKey;
  }
  return innerKey;
}

function makeCellKey(outerKey: string, innerKey: string): string {
  return `${outerKey}__${innerKey}`;
}

interface ICellLookup {
  hashtag: string;
  trending_label: string;
}

function collectUniqueInnerKeys(
  list: IXHashtagListResponse['list'],
): Set<string> {
  const keys = new Set<string>();
  list.forEach((outer) => {
    outer.groups?.forEach((inner) => {
      if (inner.group_key) {
        keys.add(inner.group_key);
      }
    });
  });
  return keys;
}

/** 将 X list 嵌套结构转为 TableCommonHook 矩阵数据 */
export function transformXResponseToMatrix(
  data: IXHashtagListResponse | null | undefined,
): IXHashtagMatrixView {
  const empty: IXHashtagMatrixView = {
    rows: [],
    columns: [],
    headerGroups: [],
    isFlatHeader: false,
  };

  if (!data?.list?.length) {
    return empty;
  }

  const groupBy = data.group_by || 'date';
  const isFlatHeader = collectUniqueInnerKeys(data.list).size <= 1;
  const columns: IXHashtagMatrixColumn[] = [];
  const headerGroups: IXHashtagHeaderGroup[] = [];
  const lookup = new Map<string, ICellLookup>();
  const rankSet = new Set<number>();

  data.list.forEach((outer) => {
    const inners = outer.groups || [];
    if (!inners.length) {
      return;
    }

    if (isFlatHeader) {
      const inner = inners[0];
      const cellKey = outer.group_key;
      columns.push({
        key: cellKey,
        outerKey: outer.group_key,
        innerKey: inner.group_key,
      });

      inner.list?.forEach((item) => {
        rankSet.add(item.rank);
        lookup.set(`${item.rank}_${cellKey}`, {
          hashtag: item.hashtag || '',
          trending_label: item.trending_label || '',
        });
      });
      return;
    }

    const children: string[] = [];
    inners.forEach((inner) => {
      const cellKey = makeCellKey(outer.group_key, inner.group_key);
      columns.push({
        key: cellKey,
        outerKey: outer.group_key,
        innerKey: inner.group_key,
      });
      children.push(cellKey);

      inner.list?.forEach((item) => {
        rankSet.add(item.rank);
        lookup.set(`${item.rank}_${cellKey}`, {
          hashtag: item.hashtag || '',
          trending_label: item.trending_label || '',
        });
      });
    });

    headerGroups.push({
      key: outer.group_key,
      title: formatOuterTitle(outer.group_key, groupBy),
      children,
    });
  });

  const ranks = Array.from(rankSet).sort((a, b) => a - b);
  const rows: IXHashtagMatrixRow[] = ranks.map((rank) => {
    const row: IXHashtagMatrixRow = { rank };
    columns.forEach((col) => {
      const cell = lookup.get(`${rank}_${col.key}`);
      row[col.key] = cell?.hashtag || '';
      row[`${col.key}__label`] = cell?.trending_label || '';
    });
    return row;
  });

  return { rows, columns, headerGroups, isFlatHeader };
}

export function getColumnHeader(
  col: IXHashtagMatrixColumn,
  groupBy: 'date' | 'country',
  isFlatHeader: boolean,
): string {
  if (isFlatHeader) {
    return formatOuterTitle(col.outerKey, groupBy);
  }
  return formatInnerTitle(col.innerKey, groupBy);
}
